import React, { useRef, useState, useCallback, useEffect } from 'react';

interface SignatureCaptureProps {
  onCapture: (signatureBase64: string) => void;
}

const SignatureCapture: React.FC<SignatureCaptureProps> = ({ onCapture }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hasDrawn, setHasDrawn] = useState(false);
  const isDrawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const container = canvas.parentElement;
    if (!container) return;

    const displayWidth = container.clientWidth;
    const displayHeight = 200;
    const dpr = window.devicePixelRatio || 1;

    canvas.width = displayWidth * dpr;
    canvas.height = displayHeight * dpr;
    canvas.style.width = `${displayWidth}px`;
    canvas.style.height = `${displayHeight}px`;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(dpr, dpr);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, displayWidth, displayHeight);
    }
  }, []);

  const getPoint = useCallback(
    (e: React.TouchEvent | React.MouseEvent): { x: number; y: number } => {
      const canvas = canvasRef.current!;
      const rect = canvas.getBoundingClientRect();
      if ('touches' in e) {
        const touch = e.touches[0];
        return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
      }
      return {
        x: (e as React.MouseEvent).clientX - rect.left,
        y: (e as React.MouseEvent).clientY - rect.top,
      };
    },
    [],
  );

  const startDrawing = useCallback(
    (e: React.TouchEvent | React.MouseEvent) => {
      e.preventDefault();
      isDrawingRef.current = true;
      const point = getPoint(e);
      lastPointRef.current = point;

      const ctx = canvasRef.current?.getContext('2d');
      if (ctx) {
        ctx.beginPath();
        ctx.moveTo(point.x, point.y);
      }
    },
    [getPoint],
  );

  const draw = useCallback(
    (e: React.TouchEvent | React.MouseEvent) => {
      e.preventDefault();
      if (!isDrawingRef.current) return;

      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;

      const point = getPoint(e);
      const last = lastPointRef.current;
      if (!last) return;

      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(point.x, point.y);
      ctx.stroke();

      lastPointRef.current = point;
      if (!hasDrawn) setHasDrawn(true);
    },
    [getPoint, hasDrawn],
  );

  const stopDrawing = useCallback(() => {
    isDrawingRef.current = false;
    lastPointRef.current = null;
  }, []);

  const handleClear = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const displayWidth = canvas.width / dpr;
    const displayHeight = canvas.height / dpr;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, displayWidth, displayHeight);
    setHasDrawn(false);
  }, []);

  const handleContinue = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const base64 = canvas.toDataURL('image/png');
    onCapture(base64);
  }, [onCapture]);

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <h1 style={styles.title}>Draw Your Signature</h1>
        <p style={styles.instructions}>
          Use your finger to draw your signature below. This will be applied to
          all documents.
        </p>

        <div style={styles.canvasWrapper}>
          <canvas
            ref={canvasRef}
            style={styles.canvas}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
          />
        </div>

        <button type="button" style={styles.clearButton} onClick={handleClear}>
          Clear
        </button>
      </div>

      <div style={styles.bottomArea}>
        <button
          type="button"
          style={{
            ...styles.continueButton,
            opacity: hasDrawn ? 1 : 0.4,
          }}
          disabled={!hasDrawn}
          onClick={handleContinue}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#f9fafb',
  },
  content: {
    flex: 1,
    padding: '24px 20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: '#111827',
    margin: '0 0 8px 0',
    textAlign: 'center',
  },
  instructions: {
    fontSize: 15,
    color: '#6b7280',
    margin: '0 0 24px 0',
    textAlign: 'center',
    lineHeight: '1.5',
  },
  canvasWrapper: {
    width: '100%',
    borderRadius: 12,
    overflow: 'hidden',
    border: '2px dashed #d1d5db',
    backgroundColor: '#ffffff',
  },
  canvas: {
    display: 'block',
    touchAction: 'none',
    cursor: 'crosshair',
  },
  clearButton: {
    marginTop: 12,
    padding: '8px 16px',
    background: 'none',
    border: 'none',
    color: '#2563eb',
    fontSize: 15,
    fontWeight: 500,
    cursor: 'pointer',
  },
  bottomArea: {
    padding: '16px 20px',
    paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
  },
  continueButton: {
    width: '100%',
    height: 52,
    borderRadius: 12,
    backgroundColor: '#2563eb',
    color: '#ffffff',
    fontSize: 17,
    fontWeight: 600,
    border: 'none',
    cursor: 'pointer',
  },
};

export default SignatureCapture;
